import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState, UserResponseStore } from '../types/graph';
import { ViewableQuestion } from '../types/global';
import { useActiveQueue } from './useActiveQueue';

const isNumericValueOutOfBounds = (
  value: number,
  min: number,
  max: number,
): boolean => {
  if (isNaN(min) && isNaN(max)) {
    return false;
  }

  if (isNaN(min)) {
    return value > max;
  }

  if (isNaN(max)) {
    return value < min;
  }

  return value < min || value > max;
};

const validateNumericQuestion = (
  question: ViewableQuestion,
  response: UserResponseStore,
): boolean => {
  if (!response || !question?.responses?.length) return false;

  const hasInvalidNumericResponse = question.responses.some((resp) => {
    if (resp.format === 'DECIMAL' || resp.format === 'INTEGER') {
      const min = Number(resp.minimum);
      const max = Number(resp.maximum);
      const value = response[resp.extId];
      return (
        value !== undefined &&
        value !== '' &&
        isNumericValueOutOfBounds(Number(value), min, max)
      );
    }
    return false;
  });

  if (hasInvalidNumericResponse) return true;

  return false;
};

export const useInvalidQuestions = () => {
  const activeQueue = useActiveQueue();
  const questions = useSelector((state: RootState) => state.kb.questions);
  const responses = useSelector((state: RootState) => state.userResponses);

  const invalidQuestions = useMemo(
    () =>
      activeQueue.filter((extId) => {
        const question = questions[extId];
        const response = responses[extId];

        const isNumericInvalid = validateNumericQuestion(question, response);

        return isNumericInvalid;
      }),
    [activeQueue, questions, responses],
  );

  return invalidQuestions;
};
